import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { formatINR } from "../lib/products";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 8;

export default function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed | expired
  const [info, setInfo] = useState(null);
  const attemptsRef = useRef(0);
  const cartClearedRef = useRef(false);
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        setInfo(data);
        if (data.payment_status === "paid") {
          if (!cartClearedRef.current) {
            cartClearedRef.current = true;
            clear();
          }
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus("failed");
          return;
        }
        setTimeout(tick, POLL_INTERVAL);
      } catch {
        if (!cancelled) setStatus("failed");
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Header />
      <section className="max-w-3xl mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
        {status === "checking" && (
          <div data-testid="checkout-checking">
            <Loader2
              className="mx-auto text-[#FF2800] animate-spin"
              size={42}
              strokeWidth={1.5}
            />
            <p className="af-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mt-6">
              // Verifying payment
            </p>
            <h1 className="af-display text-3xl md:text-4xl font-bold uppercase mt-3">
              Hold the brake…
            </h1>
            <p className="text-zinc-400 mt-3">
              We're confirming your payment with Stripe.
            </p>
          </div>
        )}

        {status === "paid" && (
          <div data-testid="checkout-success">
            <CheckCircle2
              className="mx-auto text-[#00E5FF]"
              size={56}
              strokeWidth={1.2}
            />
            <p className="af-mono text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] mt-6">
              // Lights out
            </p>
            <h1 className="af-display text-4xl md:text-5xl font-bold uppercase mt-3">
              Order confirmed
            </h1>
            <p className="text-zinc-400 mt-3">
              Thanks for racing with Apex Designs. We'll email tracking shortly.
            </p>
            {info && (
              <p
                className="af-mono text-sm text-zinc-300 mt-6"
                data-testid="checkout-amount"
              >
                Total charged: {formatINR(info.amount_total)}
              </p>
            )}
            <Link
              to="/shop"
              className="inline-flex items-center mt-10 bg-[#FF2800] text-white font-semibold uppercase tracking-wider py-4 px-8 hover:bg-[#CC2000] transition-colors"
              data-testid="checkout-continue-shop"
            >
              Continue shopping
            </Link>
          </div>
        )}

        {(status === "failed" || status === "expired") && (
          <div data-testid="checkout-failed">
            <AlertTriangle
              className="mx-auto text-[#FF2800]"
              size={48}
              strokeWidth={1.2}
            />
            <p className="af-mono text-[10px] uppercase tracking-[0.3em] text-[#FF2800] mt-6">
              // Pit penalty
            </p>
            <h1 className="af-display text-3xl md:text-4xl font-bold uppercase mt-3">
              Something went wrong
            </h1>
            <p className="text-zinc-400 mt-3">
              Your payment did not complete. No money was deducted. Please try
              again.
            </p>
            <Link
              to="/shop"
              className="inline-flex items-center mt-10 border border-white/30 px-8 py-4 af-mono text-[11px] uppercase tracking-[0.25em] hover:bg-white hover:text-black transition-colors"
              data-testid="checkout-retry"
            >
              Back to shop
            </Link>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
